import type { AppIdentity, BillingAccessState } from '../../lib/billing.ts';
import { currentRuntime } from '../../config/runtime';
import { resolveTenantProductName, type WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';
import { getBranding } from '../../config/branding';
import { getAppLogoSrc } from '../../constants/branding';
import { createCheckoutSession } from '../../lib/billing.ts';
import { AbandonedCheckoutPage } from '../pages/AbandonedCheckoutPage';
import { ReactivateSubscriptionPage } from '../pages/ReactivateSubscriptionPage';
import { useState } from 'react';
import { isGlobalBillingBypassEnabled } from '../../config/runtime/billingBypass';

type BillingAccessGateProps = {
  identity: AppIdentity;
  billingState: BillingAccessState | null;
  onRefresh: () => Promise<void>;
  tenantLandingUrl?: string;
  onSignInToExistingAccount?: () => void;
  whiteLabel?: WhiteLabelAppBranding | null;
};

function normalizeBillingStatus(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, ' ');
}

function hasConsumedTrial(state: BillingAccessState | null): boolean {
  return Boolean(state?.trialUsedAt || state?.trial_used_at);
}

function hasConfirmedSubscriptionHistory(state: BillingAccessState | null): boolean {
  const normalizedStatus = normalizeBillingStatus(state?.subscriptionStatus || state?.status);
  if (!state?.stripeSubscriptionId) {
    return false;
  }

  return !['', 'inactive', 'incomplete', 'incomplete expired'].includes(normalizedStatus);
}

function isReactivationState(state: BillingAccessState | null): boolean {
  return hasConsumedTrial(state) || hasConfirmedSubscriptionHistory(state);
}

function SubscriptionRequiredFallback({ onRefresh }: { onRefresh: () => Promise<void> }) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const brandName = resolveTenantProductName();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B101E] p-6 text-slate-200">
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900/70 p-8 text-center">
        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">{brandName}</div>
        <h1 className="mt-4 text-3xl font-semibold text-white">Subscription required</h1>
        <p className="mt-3 text-sm text-slate-400">
          Your workspace is still locked until billing confirms access.
        </p>
        <button
          type="button"
          onClick={() => { void handleRefresh(); }}
          disabled={isRefreshing}
          className="mt-6 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isRefreshing ? 'Checking access...' : 'Retry access check'}
        </button>
      </div>
    </div>
  );
}

export default function BillingAccessGate({ identity, billingState, onRefresh, tenantLandingUrl = '/', onSignInToExistingAccount, whiteLabel }: BillingAccessGateProps) {
  const branding = getBranding(currentRuntime.brand.id);
  const [isLoading, setIsLoading] = useState(false);

  if (isGlobalBillingBypassEnabled()) {
    return null;
  }

  // The server's access decision is authoritative. Keep this component from
  // ever presenting a payment surface if a stale route decision reaches it
  // after an access-granted response has arrived.
  if (billingState?.hasAccess === true || billingState?.isAdminBillingBypass === true) {
    return null;
  }

  const startCheckout = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const checkoutUrl = await createCheckoutSession(identity, { plan: 'monthly' });
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('[BillingAccessGate] checkout error', err);
      setIsLoading(false);
    }
  };

  if (isReactivationState(billingState)) {
    return (
      <ReactivateSubscriptionPage
        tenantName={resolveTenantProductName()}
        productName={branding.copy.billing.planName}
        monthlyPrice={branding.copy.billing.pricing.monthly.priceLabel}
        logoSrc={getAppLogoSrc({ brand: branding, variant: 'horizontalSplit', theme: 'dark' })}
        whiteLabel={whiteLabel}
        isLoading={isLoading}
        onReactivate={() => { void startCheckout(); }}
      />
    );
  }

  if (!billingState || !billingState.stripeSubscriptionId) {
    return (
      <AbandonedCheckoutPage
        tenantName={resolveTenantProductName()}
        productName={branding.copy.billing.planName}
        monthlyPrice={branding.copy.billing.pricing.monthly.priceLabel}
        trialDays={branding.copy.billing.trialDays ?? 7}
        logoSrc={getAppLogoSrc({ brand: branding, variant: 'horizontalSplit', theme: 'dark' })}
        whiteLabel={whiteLabel}
        isLoading={isLoading}
        tenantLandingUrl={tenantLandingUrl || (currentRuntime.brand.id === 'warrior-trading' ? 'https://warriortrading.com' : '/')}
        onSignInToExistingAccount={onSignInToExistingAccount}
        onStartCheckout={() => { void startCheckout(); }}
      />
    );
  }

  return <SubscriptionRequiredFallback onRefresh={onRefresh} />;
}
